import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sneaker, EnvelopeSimple, CheckCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  { value: 'general', label: 'General enquiry' },
  { value: 'affiliate', label: 'Retailer / affiliate partnership' },
  { value: 'press', label: 'Press & media' },
  { value: 'bug', label: 'Report a bug or issue' },
  { value: 'product', label: 'Product / pricing correction' },
  { value: 'account', label: 'Account help' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', topic: 'general', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill out every field.');
      return;
    }
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.detail || 'Something went wrong. Please email hello@solefinder.app directly.');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-12 py-12">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Sneaker size={24} weight="duotone" className="text-[#FF3B30]" />
          <span className="font-heading font-black text-lg tracking-tighter uppercase">SOLEFINDER</span>
        </Link>

        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors mb-8"
          data-testid="contact-back-btn"
        >
          <span>&larr;</span> Back to Home
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter uppercase mb-4" data-testid="contact-title">
              Get in<br /><span className="text-[#FF3B30]">touch</span>
            </h1>
            <p className="text-[#52525B] text-base leading-relaxed mb-6">
              Questions, feedback, a shoe we should add, or a retailer partnership?
              Drop us a note and we'll reply within 1&ndash;2 business days.
            </p>
            <div className="space-y-3 text-sm text-[#52525B]">
              <div className="flex items-center gap-2">
                <EnvelopeSimple size={18} weight="duotone" className="text-[#FF3B30]" />
                <a href="mailto:hello@solefinder.app" className="hover:text-[#FF3B30] transition-colors" data-testid="contact-email-link">
                  hello@solefinder.app
                </a>
              </div>
              <p className="text-xs text-[#A1A1AA]">
                For press enquiries please include your outlet and deadline. For affiliate partners include your network (CJ / Awin / Impact / Rakuten / Partnerize).
              </p>
            </div>
          </div>

          <div className="md:col-span-3">
            {status === 'sent' ? (
              <div className="border-2 border-[#10B981] bg-[#F0FDF4] p-8 text-center" data-testid="contact-success">
                <CheckCircle size={48} weight="duotone" className="text-[#10B981] mx-auto mb-3" />
                <h2 className="font-heading font-black text-2xl tracking-tighter uppercase mb-2">Message sent</h2>
                <p className="text-[#52525B] text-sm mb-6">
                  Thanks {form.name.split(' ')[0] || 'for reaching out'}! We'll reply to <strong>{form.email}</strong> within 1&ndash;2 business days.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-6 py-3 text-xs hover:bg-[#FF3B30] transition-colors"
                  data-testid="contact-success-home-btn"
                >
                  Back to home
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 border-2 border-[#0A0A0A] p-6" data-testid="contact-form">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update('name')}
                    required
                    className="w-full border-2 border-[#E4E4E7] focus:border-[#0A0A0A] outline-none px-3 py-2.5 text-sm"
                    data-testid="contact-name-input"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    required
                    className="w-full border-2 border-[#E4E4E7] focus:border-[#0A0A0A] outline-none px-3 py-2.5 text-sm"
                    data-testid="contact-email-input"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Topic</label>
                  <select
                    value={form.topic}
                    onChange={update('topic')}
                    className="w-full border-2 border-[#E4E4E7] focus:border-[#0A0A0A] outline-none px-3 py-2.5 text-sm bg-white"
                    data-testid="contact-topic-select"
                  >
                    {TOPICS.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Message</label>
                  <textarea
                    value={form.message}
                    onChange={update('message')}
                    required
                    rows={6}
                    className="w-full border-2 border-[#E4E4E7] focus:border-[#0A0A0A] outline-none px-3 py-2.5 text-sm resize-y"
                    data-testid="contact-message-input"
                    placeholder="Tell us what's on your mind..."
                  />
                </div>
                {error && (
                  <p className="text-xs font-bold text-[#FF3B30]" data-testid="contact-error-msg">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-3.5 text-xs hover:bg-[#D62828] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  <PaperPlaneTilt size={16} weight="bold" />
                  {status === 'sending' ? 'Sending...' : 'Send message'}
                </button>
                <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                  By submitting you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>. We only use your details to respond to your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
